import React from "react";
import * as Yup from "yup";
import {
  Alert,
  Dialog,
  Divider,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Stack,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import {
  UpdateEligibilityDialogProps,
  UpdateEligibilityFormValues,
} from "./types";
import { useUpdateEligibilityDialogContext } from "./context";
import { Formik, useFormikContext } from "formik";
import InternetConnectionAddress from "./InternetConnectionAddress";
import { getFormattedAddress } from "../helpers";
import { PremiseDetails } from "../types";
import UpdateEligibilityActions from "./UpdateEligibilityActions";
import { validateUpdateEligibility } from "src/components/Form/FormComponents/Validations";
import { apiClient } from "src/api/apiService";
import {
  mapInternetStatusLookupRequest,
  mapToFormValuesInternet,
} from "src/api/apiHelper";
import { FormValues } from "src/components/Form/types";
import { useDashboardContext } from "src/templates/Dashboard/context";

export const UpdateEligibilityDialogContent = () => {
  const { closeDialog, id } = useUpdateEligibilityDialogContext();
  const { values, setFieldValue, errors, status, setStatus } =
    useFormikContext<UpdateEligibilityFormValues>();
  const addressString = getFormattedAddress(values.addressObj);
  //console.log(addressString)
  return (
    <Dialog
      id={id}
      open={true}
      title="Update Eligibility"
      onClose={() => closeDialog()}
      maxWidth="xl"
      stickyTitle
    >
      <Stack space="xlarge">
        {/* Premise Address Section */}
        <Stack space="xxsmall">
          <Stack space="xsmall">
            <Text tone="neutral" variant="body-small">
              Premise address
            </Text>
            <Text>{addressString}</Text>
          </Stack>
          <Divider />
        </Stack>
        {/* End Premise Address Section */}

        {/* Instructions */}
        <Stack space="medium">
          <Text>
            To determine eligibility, first visit{" "}
            <TextLink
              href="https://www.originenergy.com.au/internet/plans/"
              target="_blank"
            >
              compare internet plans
            </TextLink>{" "}
            and check if address is eligible for retail. If the address cannot
            be found, try searching:
          </Text>
          <List space="small">
            <ListItem>using street number and street name only</ListItem>
            <ListItem>using street name only</ListItem>
          </List>
          <Text>
            If Origin does not retail, check if the address is eligible for
            pre-install using{" "}
            <TextLink
              href="https://www.nbnco.com.au/develop-or-plan-with-the-nbn/new-developments/pre-installation-request/new"
              target="_blank"
            >
              NBN pre-install form
            </TextLink>
            .
          </Text>
        </Stack>
        {/* End Instructions */}

        {/* Radio Group */}
        <RadioGroup
          name="eligibilityStatus"
          label="What is the Internet eligibility status of the premise?"
          value={values.eligibilityStatus}
          onChange={(e) => {
            // Clear submit-level error after any user change.
            if (status?.submitError) {
              setStatus(undefined);
            }
            setFieldValue("eligibilityStatus", e.target.value);
          }}
          helperText={errors.eligibilityStatus}
          error={!!errors.eligibilityStatus}
        >
          <Radio
            label="Eligible for retail"
            id="eligibility-retail"
            value="retail"
          />
          <Radio
            label="Eligible for pre-installation"
            id="eligibility-pre-install"
            value="pre-install"
          />
          <Radio
            label="Pre-installation in progress"
            id="eligibility-in-progress"
            value="in-progress"
          />
          <Radio
            label="Not eligible for pre-installation"
            id="eligibility-not-eligible"
            value="not-eligible"
            helperText="This will cancel the request for Internet at this premise."
          />
        </RadioGroup>
        {/* End Radio Group */}

        {/* NBN Pre-install Form */}
        {!!values.eligibilityStatus &&
          values.eligibilityStatus !== "not-eligible" && (
            <InternetConnectionAddress />
          )}
        {/* End NBN Pre-install Form */}

        {!!status?.submitError && (
          <Alert tone="critical" title="Failed to update request">
            Please try again or come back later.
          </Alert>
        )}

        {/* Actions */}
        <UpdateEligibilityActions />
        {/* End Actions */}
      </Stack>
    </Dialog>
  );
};

export const UpdateEligibilityDialog: React.FC<UpdateEligibilityDialogProps> = (
  props
) => {
  const { closeDialog } = useUpdateEligibilityDialogContext();
  const { snackbarSection } = useDashboardContext();
  const internetService: any = props.services?.find(
    (s: any) => s.service_type === "INTERNET"
  );
  const knockdownFutureState = internetService?.knockdown_future_state || "";
  const initialValues: UpdateEligibilityFormValues = {
    addressObj: props.premise as PremiseDetails,
    eligibilityStatus: "",
    unitNumber: "",
    premiseType: "",
    lotNumber: "",
    streetNumber: "",
    streetName: "",
    streetSuffix: "",
    streetType: "",
    suburb: "",
    state: "",
    postcode: "",
    knockdownFutureState,
  };

  const handleSubmit = async (
    values: UpdateEligibilityFormValues,
    { setStatus }: { setStatus: (status?: { submitError?: string }) => void }
  ) => {
    setStatus(undefined);
    try {
      const formValues = mapToFormValuesInternet(values);
      let statusLookupData: any;

      if (values.eligibilityStatus !== "not-eligible") {
        const { data } = await apiClient.post(
          `/internet/status-look-up`,
          mapInternetStatusLookupRequest(formValues as FormValues)
        );
        statusLookupData = data;
      }

      const connectionTypeMap: Record<string, string> = {
        retail: "CAN_RETAIL",
        "pre-install": "PRE_INSTALL_NBN",
        "in-progress": "PRE_INSTALL_NBN",
        "not-eligible": "ADDRESS_NOT_FOUND",
      };
      const connectionType = connectionTypeMap[values.eligibilityStatus];
      if (
        connectionType !== statusLookupData?.outcome.status &&
        connectionType !== "ADDRESS_NOT_FOUND"
      ) {
        console.warn(
          "Selected eligibility status does not match API response:",
          {
            selected: values.eligibilityStatus,
            apiResponse: statusLookupData?.outcome.status,
          }
        );
        throw new Error("Eligibility status mismatch");
      }

      let payloadConnectionType = connectionType;
      if (
        connectionType === "PRE_INSTALL_NBN" &&
        knockdownFutureState === "CAN_RETAIL"
      ) {
        payloadConnectionType = "CAN_RETAIL";
      }
      // Call update-connection-eligibility API
      const updatePayload =
        connectionType === "ADDRESS_NOT_FOUND"
          ? {
              request_id: internetService?.request_id,
              connection_type: payloadConnectionType,
            }
          : {
              request_id: internetService?.request_id,
              nbn_address: statusLookupData?.anex_address,
              service_identifier: statusLookupData?.premise?.loc_id,
              connection_type: payloadConnectionType,
            };

      await apiClient.put(
        `/internet/update-connection-eligibility`,
        updatePayload
      );
    } catch (error) {
      console.error("Failed to call API:", error);
      setStatus({ submitError: "Failed to update request" });
      return;
    }
    props.reFetchPremises();
    closeDialog();
    snackbarSection.show({
      children: "Request updated.",
      showClose: true,
      autoHideDuration: 5000,
      tone: "positive",
    });
  };

  const validationSchema = Yup.lazy((values?: UpdateEligibilityFormValues) =>
    !!values?.eligibilityStatus && values?.eligibilityStatus !== "not-eligible"
      ? validateUpdateEligibility()
      : Yup.object().shape({
          eligibilityStatus: Yup.string().required(
            "Oops, you must select an option."
          ),
        })
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      <UpdateEligibilityDialogContent />
    </Formik>
  );
};
